import { useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { ProductGroupBy, ProductPerfRow } from '@/api/bi';

type Metric = 'gmv' | 'units' | 'margin' | 'delta';

const METRICS: { value: Metric; label: string }[] = [
  { value: 'gmv', label: 'GMV' },
  { value: 'units', label: 'Units' },
  { value: 'margin', label: 'Margin' },
  { value: 'delta', label: 'Movers' },
];

interface ProductLeaderboardsProps {
  rows: ProductPerfRow[];
  loading?: boolean;
  dimension: ProductGroupBy;
  onRowClick?: (row: ProductPerfRow) => void;
}

/** Top/bottom performers for the active dimension, switchable by metric. */
export function ProductLeaderboards({ rows, loading, dimension, onRowClick }: ProductLeaderboardsProps) {
  const [metric, setMetric] = useState<Metric>('gmv');

  const { top, bottom } = useMemo(() => {
    const valued = rows.filter((r) => valueFor(r, metric) != null);
    const sorted = [...valued].sort((a, b) => (valueFor(b, metric) ?? 0) - (valueFor(a, metric) ?? 0));
    return { top: sorted.slice(0, 5), bottom: sorted.slice(-5).reverse() };
  }, [rows, metric]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Leaderboard · {dimension.replace('_', ' ')}</CardTitle>
        <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-surface p-0.5 text-[11px]">
          {METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMetric(m.value)}
              aria-pressed={metric === m.value}
              className={cn(
                'press inline-flex h-6 items-center rounded-sm px-2 font-medium transition-colors focus-ring',
                metric === m.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-56 w-full" />
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No data in range.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <RankList title="Top 5" rows={top} metric={metric} onRowClick={onRowClick} />
            <RankList title="Bottom 5" rows={bottom} metric={metric} onRowClick={onRowClick} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function RankList({
  title,
  rows,
  metric,
  onRowClick,
}: {
  title: string;
  rows: ProductPerfRow[];
  metric: Metric;
  onRowClick?: (row: ProductPerfRow) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <ul className="space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.id}>
            <button
              type="button"
              disabled={!onRowClick}
              onClick={() => onRowClick?.(r)}
              className={cn(
                'flex w-full items-center gap-2 text-xs text-left',
                onRowClick ? 'press rounded-md px-1 py-0.5 hover:bg-muted focus-ring' : '',
              )}
            >
              <span className="kpi-num w-4 text-right text-muted-foreground">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-foreground">{r.name}</span>
              <MetricValue row={r} metric={metric} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MetricValue({ row, metric }: { row: ProductPerfRow; metric: Metric }) {
  if (metric === 'delta') {
    const pct = row.gmv_delta_pct;
    if (pct == null) return <span className="text-muted-foreground">—</span>;
    const positive = pct > 0;
    return (
      <span className={cn('inline-flex items-center gap-0.5 font-medium', positive ? 'text-success' : 'text-danger')}>
        {positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
        {fmt.delta(pct, { unit: 'pct' })}
      </span>
    );
  }
  if (metric === 'units') return <span className="kpi-num font-medium">{fmt.number(row.units)}</span>;
  if (metric === 'margin')
    return <span className="kpi-num font-medium">{row.margin_pct != null ? fmt.pct(row.margin_pct) : '—'}</span>;
  return <span className="kpi-num font-medium">{fmt.npr(row.gmv)}</span>;
}

function valueFor(r: ProductPerfRow, metric: Metric): number | null {
  if (metric === 'gmv') return r.gmv;
  if (metric === 'units') return r.units;
  if (metric === 'margin') return r.margin_pct;
  return r.gmv_delta_pct;
}
